import { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Button } from "design_system_radar/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design_system_radar/command";
import { EngineLiveIcon } from "design_system_radar/icons/EngineLiveIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "design_system_radar/popover";
import { toast } from "design_system_radar/sonner";
import { LoaderCircle } from "lucide-react";

import { useGroupsSteps } from "@/hooks/useGroupsSteps";
import { useReadOnlyUser } from "@/modules/auth/useReadOnlyUser";
import { engineExecute } from "@/services/movements";

interface ConciliarButtonProps {
  expanded?: boolean;
}

export function ConciliarButton({ expanded = true }: ConciliarButtonProps) {
  const groups = useGroupsSteps((state) => state.groups);
  const fetchGroups = useGroupsSteps((state) => state.fetchGroups);
  const isReadOnlyUser = useReadOnlyUser();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (groups.length === 0) fetchGroups();
  }, [groups.length, fetchGroups]);

  const runEngineMutation = useMutation({
    mutationKey: ["runEngine"],
    mutationFn: async (groupId: string) => {
      await engineExecute({ groups: [groupId] });
    },
    onSuccess: () => {
      toast.success("Se está ejecutando el motor de conciliación");
    },
    onError: () => {
      toast.error("No ha sido posible ejecutar el motor");
    },
  });

  const isDisabled =
    runEngineMutation.isPending || isReadOnlyUser || groups.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          className={expanded ? "w-34 h-9" : "w-12 h-9 rounded-xl p-0"}
          disabled={isDisabled}
        >
          <div className="flex items-center justify-center w-full gap-2">
            {runEngineMutation.isPending ? (
              <LoaderCircle className="animate-spin" size={28} />
            ) : (
              <EngineLiveIcon size={28} />
            )}
            {expanded &&
              (runEngineMutation.isPending ? "Procesando..." : "Conciliar")}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" side="right" align="start">
        <Command>
          <CommandInput placeholder="Buscar grupo..." />
          <CommandList>
            <CommandEmpty>No se encontraron grupos.</CommandEmpty>
            <CommandGroup heading="Seleccionar grupo">
              {groups.map((group) => (
                <CommandItem
                  key={group._id}
                  value={group.name}
                  onSelect={() => {
                    runEngineMutation.mutate(group._id);
                    setOpen(false);
                  }}
                >
                  {group.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
